const SNAPSHOT_MUTATION_CHANNEL = 'variorum-indexeddb-snapshot-mutations-v1'

type MutationMessage = {
  version: 1
  databaseName: string
  mutationId: string
}

export interface IndexedDBSnapshotGuard {
  assertStable: () => Promise<void>
  close: () => void
}

export class IndexedDBSnapshotChangedError extends Error {
  constructor() {
    super('The browser database changed while the PostgreSQL checkpoint was running.')
    this.name = 'IndexedDBSnapshotChangedError'
  }
}

export class IndexedDBSnapshotGuardUnavailableError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'IndexedDBSnapshotGuardUnavailableError'
  }
}

const instrumentedDatabases = new WeakSet<IDBDatabase>()
const instrumentedDatabaseNames = new Set<string>()
let localMutationEpoch = 0
let mutationSequence = 0
let publisher: BroadcastChannel | null = null

const getPublisher = (): BroadcastChannel | null => {
  if (publisher) {
    return publisher
  }
  if (typeof BroadcastChannel !== 'function') {
    return null
  }
  try {
    publisher = new BroadcastChannel(SNAPSHOT_MUTATION_CHANNEL)
    ;(publisher as BroadcastChannel & { unref?: () => void }).unref?.()
    return publisher
  } catch {
    return null
  }
}

const markMutation = (databaseName: string): void => {
  localMutationEpoch += 1
  mutationSequence += 1
  const message: MutationMessage = {
    version: 1,
    databaseName,
    mutationId: `${Date.now()}-${mutationSequence}`,
  }
  try {
    getPublisher()?.postMessage(message)
  } catch {
    // Same-document detection still invalidates the active checkpoint.
  }
}

/**
 * Observe every read/write transaction opened through one of the app-owned
 * IndexedDB connections. The wrapper does not alter transaction scheduling or
 * failure behavior; it only publishes after a write transaction commits.
 */
export const instrumentIndexedDBWriteTransactions = (
  database: IDBDatabase,
  databaseName: string,
): void => {
  if (instrumentedDatabases.has(database)) {
    return
  }
  const originalTransaction = database.transaction.bind(database)
  const wrappedTransaction = (
    storeNames: string | string[],
    mode: IDBTransactionMode = 'readonly',
    options?: IDBTransactionOptions,
  ): IDBTransaction => {
    const transaction = options === undefined
      ? originalTransaction(storeNames, mode)
      : originalTransaction(storeNames, mode, options)
    if (mode === 'readwrite') {
      transaction.addEventListener('complete', () => markMutation(databaseName), {
        once: true,
      })
    }
    return transaction
  }

  try {
    Object.defineProperty(database, 'transaction', {
      configurable: true,
      value: wrappedTransaction,
    })
  } catch (error) {
    throw new IndexedDBSnapshotGuardUnavailableError(
      `Could not instrument ${databaseName} for checkpoint consistency: ${
        error instanceof Error ? error.message : 'unknown browser error'
      }`,
    )
  }
  instrumentedDatabases.add(database)
  instrumentedDatabaseNames.add(databaseName)
}

const nextTask = (): Promise<void> =>
  new Promise(resolve => globalThis.setTimeout(resolve, 0))

export const beginIndexedDBSnapshotGuard = ({
  requiredDatabaseNames,
  requireCrossDocument = true,
}: {
  requiredDatabaseNames: readonly string[]
  requireCrossDocument?: boolean
}): IndexedDBSnapshotGuard => {
  const missingDatabase = requiredDatabaseNames.find(
    databaseName => !instrumentedDatabaseNames.has(databaseName),
  )
  if (missingDatabase) {
    throw new IndexedDBSnapshotGuardUnavailableError(
      `${missingDatabase} is not instrumented for checkpoint consistency.`,
    )
  }

  let channel: BroadcastChannel | null = null
  let remoteMutationSeen = false
  if (typeof BroadcastChannel === 'function') {
    try {
      channel = new BroadcastChannel(SNAPSHOT_MUTATION_CHANNEL)
      channel.addEventListener('message', () => {
        remoteMutationSeen = true
      })
    } catch {
      channel = null
    }
  }
  if (requireCrossDocument && !channel) {
    throw new IndexedDBSnapshotGuardUnavailableError(
      'This browser cannot detect IndexedDB writes from another tab.',
    )
  }

  const startingEpoch = localMutationEpoch
  let closed = false
  const throwIfChanged = (): void => {
    if (
      !closed &&
      (localMutationEpoch !== startingEpoch || remoteMutationSeen)
    ) {
      throw new IndexedDBSnapshotChangedError()
    }
  }

  return {
    assertStable: async () => {
      throwIfChanged()
      // BroadcastChannel delivery is task-queued. Two turns keep a just-finished
      // cross-tab transaction from racing the final checkpoint assertion.
      await nextTask()
      await nextTask()
      throwIfChanged()
    },
    close: () => {
      closed = true
      channel?.close()
    },
  }
}
